/**
 * TrainingControls — Left sidebar with all training mode controls.
 *
 * Sections: display mode, trick timeline toggle, bot management,
 * deal configuration, game actions, coach panel.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import CoachChat from './CoachChat';
import type { CoachMessage } from './CoachChat';
import CollapsibleSection from './CollapsibleSection';
import { SidebarCollapseIcon, ListBulletsIcon } from './Icons';
import ExpertFeedbackPanel from './ExpertFeedbackPanel';
import type { MLSuggestion, FeedbackSubmitData } from './ExpertFeedbackPanel';
import TrainingDealSetup from './TrainingDealSetup';
import type { Socket } from 'socket.io-client';
import type { TrainingAIModelSelection, TrainingAIModelSlot, TrainingDisplayMode, TrainingDealConstraints } from '../types/app';
import type { CardNumber } from '../types/game';
import type { GameSpeed } from '../pages/TrainingPage';
import {
  toTrainingDealConstraints,
  validateHandQualities,
  type TrainingDealSetupValue,
} from '../utils/trainingDealSetup';

interface TrainingControlsProps {
  socket: Socket | null;
  displayMode: TrainingDisplayMode;
  showTimeline: boolean;
  disabledBots: Set<number>;
  humanAutomated: boolean;
  phase: string | number;
  currentPlayer: number;
  myPosition: number;
  isWaitingRebid: boolean;
  players: Array<{ pseudo: string; position: number; isBot: boolean }>;
  canAskAdvice?: boolean;
  gameSpeed?: GameSpeed;
  coachMessages: CoachMessage[];
  onAddCoachMessage: (msg: Omit<CoachMessage, 'id' | 'timestamp'>) => void;
  adviceDetailed?: boolean;
  onToggleAdviceDetailed?: () => void;
  onToggleDisplayMode: () => void;
  onToggleTimeline: () => void;
  onRotateBoard: () => void;
  onToggleBot: (seat: number) => void;
  onNewDeal: (constraints?: TrainingDealConstraints) => void;
  onRestartSameDeal: () => void;
  onAbandon: () => void;
  onRebidAfterPass: () => void;
  onForceBid: (seat: number, contract: number) => void;
  onSpeedChange?: (speed: GameSpeed) => void;
  canShowFeedback?: boolean;
  feedbackMode?: boolean;
  onToggleFeedbackMode?: (enabled: boolean) => void;
  mlSuggestion?: MLSuggestion | null;
  feedbackSeat?: number;
  legalMoves?: CardNumber[];
  hand?: CardNumber[];
  trickIdx?: number;
  positionInTrick?: number;
  gameState?: Record<string, unknown>;
  onSubmitFeedback?: (data: FeedbackSubmitData) => void;
  feedbackCardClickRef?: React.MutableRefObject<((card: CardNumber) => void) | null>;
  onAddToLibrary?: () => void;
  useBetaModel?: boolean;
  botModelSlot?: 'production' | 'beta';
  onToggleBotModelSlot?: (slot: 'production' | 'beta') => void;
  isAdmin?: boolean;
  aiModelSelection?: TrainingAIModelSelection;
  onAIModelSelectionChange?: (selection: TrainingAIModelSelection) => void;
  dealSetup: TrainingDealSetupValue;
  onDealSetupChange: (next: TrainingDealSetupValue) => void;
  taker?: number | null;
  viewPosition?: number;
  /** Masque la colonne gauche (layout large) pour agrandir la table */
  onHideLeftColumn?: () => void;
  /** Liste de donnes (replay comparateur ML admin, défi CIA duplicate), si plus d’une donne ou variantes */
  compareReplayBatch?: {
    title: string;
    deals: Array<{
      dealIndex: number;
      label: string;
      variants?: Array<{
        tableIdx: number;
        label: string;
        scoreText?: string;
        playerLabel?: string;
        teamLabel?: string;
      }>;
    }>;
    selectedIndex: number;
    selectedVariantIndex: number;
    /** Mode deux tables liées : les deux scores de la donne sélectionnée sont actifs. */
    dualSelectBothVariants?: boolean;
  } | null;
  onSelectCompareReplayDeal?: (index: number, variantIndex?: number) => void;
}

const POSITION_NAMES: Record<number, string> = {
  0: 'Sud (vous)',
  1: 'Est',
  2: 'Nord',
  3: 'Ouest',
};

type SidebarTab = 'settings' | 'coach' | 'dealList';

const SPEED_OPTIONS: { value: GameSpeed; label: string }[] = [
  { value: 'fast', label: 'Rapide' },
  { value: 'normal', label: 'Normal' },
  { value: 'slow', label: 'Lent' },
  { value: 'click', label: 'Clic' },
];

const AI_MODEL_OPTIONS: { value: TrainingAIModelSlot; label: string }[] = [
  { value: 'production', label: 'ML Production' },
  { value: 'beta', label: 'ML Beta' },
  { value: 'legacy-rv', label: 'Legacy RV' },
];

export default function TrainingControls({
  socket, displayMode, showTimeline, disabledBots, humanAutomated, phase,
  currentPlayer, myPosition, isWaitingRebid, players, canAskAdvice,
  gameSpeed, coachMessages, onAddCoachMessage,
  adviceDetailed, onToggleAdviceDetailed,
  onToggleDisplayMode, onToggleTimeline, onRotateBoard, onToggleBot,
  onNewDeal, onRestartSameDeal, onAbandon, onRebidAfterPass, onForceBid,
  onSpeedChange,
  canShowFeedback, feedbackMode, onToggleFeedbackMode,
  mlSuggestion, feedbackSeat, legalMoves, hand, trickIdx, positionInTrick, gameState,
  onSubmitFeedback, feedbackCardClickRef, onAddToLibrary,
  useBetaModel, botModelSlot, onToggleBotModelSlot,
  isAdmin, aiModelSelection, onAIModelSelectionChange,
  dealSetup, onDealSetupChange,
  taker, viewPosition,
  onHideLeftColumn,
  compareReplayBatch,
  onSelectCompareReplayDeal,
}: TrainingControlsProps) {
  const [forceSeat, setForceSeat] = useState(0);
  const [forceContract, setForceContract] = useState(2);
  const [activeTab, setActiveTab] = useState<SidebarTab>('settings');
  const lastCompareBatchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!compareReplayBatch) {
      lastCompareBatchKeyRef.current = null;
      if (activeTab === 'dealList') {
        setActiveTab('settings');
      }
      return;
    }
    const batchKey = `${compareReplayBatch.title}|${compareReplayBatch.deals.map((d) => d.dealIndex).join(',')}`;
    if (lastCompareBatchKeyRef.current !== batchKey) {
      lastCompareBatchKeyRef.current = batchKey;
      setActiveTab('dealList');
    }
  }, [compareReplayBatch, activeTab]);

  const isPlaying = phase === 'playing' || phase === 6;

  const constraintValidation = useMemo(
    () => validateHandQualities(dealSetup.handQualities),
    [dealSetup.handQualities],
  );

  const handleNewDealWithConstraints = useCallback(() => {
    if (!constraintValidation.valid) return;
    onNewDeal(toTrainingDealConstraints(dealSetup) ?? {});
  }, [dealSetup, onNewDeal, constraintValidation.valid]);

  const handleForceBid = useCallback(() => {
    onForceBid(forceSeat, forceContract);
  }, [forceSeat, forceContract, onForceBid]);

  const handleAIModelChange = useCallback((key: keyof TrainingAIModelSelection, value: TrainingAIModelSlot) => {
    if (!aiModelSelection || !onAIModelSelectionChange) return;
    onAIModelSelectionChange({ ...aiModelSelection, [key]: value });
  }, [aiModelSelection, onAIModelSelectionChange]);

  return (
    <div className="training-controls">
      <div className="training-controls-header">
        <div className="training-controls-header-top">
          <h3>Entraînement</h3>
          {onHideLeftColumn && (
            <button
              type="button"
              className="training-sidebar-hide-btn"
              title="Masquer cette colonne"
              aria-label="Masquer la colonne Entraînement"
              onClick={onHideLeftColumn}
            >
              <SidebarCollapseIcon size={18} />
            </button>
          )}
        </div>
        <span className="training-phase-label">
          {getPhaseLabel(phase)}
        </span>
      </div>

      {/* Tab bar */}
      <div className="training-sidebar-tab-bar">
        <button
          className={`training-sidebar-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Réglages
        </button>
        <button
          className={`training-sidebar-tab ${activeTab === 'coach' ? 'active' : ''}`}
          onClick={() => setActiveTab('coach')}
        >
          Professeur
        </button>
        {compareReplayBatch && onSelectCompareReplayDeal ? (
          <button
            type="button"
            className={`training-sidebar-tab training-sidebar-tab--icon-only ${activeTab === 'dealList' ? 'active' : ''}`}
            title={compareReplayBatch.title}
            aria-label="Choisir une donne dans la liste"
            onClick={() => setActiveTab('dealList')}
          >
            <ListBulletsIcon size={18} />
          </button>
        ) : null}
      </div>

      {/* Liste donnes comparateur ML */}
      {activeTab === 'dealList' && compareReplayBatch && onSelectCompareReplayDeal && (
        <div className="training-sidebar-tab-content training-sidebar-tab-content--deal-list">
          <div className="training-compare-deal-list-header">
            <span className="training-compare-deal-list-title">{compareReplayBatch.title}</span>
            <span className="training-compare-deal-list-count">
              {compareReplayBatch.deals.length} donne{compareReplayBatch.deals.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="training-compare-deal-list-scroll">
            {compareReplayBatch.deals.some((d) => (d.variants?.length ?? 0) > 1) && (
              <div className="training-compare-deal-list-columns">
                <span className="training-compare-deal-num-head">#</span>
                {(compareReplayBatch.deals.find((d) => (d.variants?.length ?? 0) > 1)?.variants ?? []).map(
                  (v) => (
                    <span
                      key={`head-t${v.tableIdx}`}
                      className="training-compare-deal-col-head"
                      title={v.teamLabel ?? v.playerLabel ?? v.label}
                    >
                      {v.teamLabel ?? v.playerLabel ?? `T${v.tableIdx}`}
                    </span>
                  ),
                )}
              </div>
            )}
            <ul className="training-compare-deal-list">
              {compareReplayBatch.deals.map((d, index) => {
                const hasVariants = (d.variants?.length ?? 0) > 1;
                const isSelectedDeal = index === compareReplayBatch.selectedIndex;
                return (
                  <li key={`${d.dealIndex}-${index}`}>
                    {hasVariants ? (
                      <div className="training-compare-deal-row">
                        <span className="training-compare-deal-num">{d.dealIndex}</span>
                        {d.variants!.map((v, variantIndex) => {
                          const isSelected = isSelectedDeal && (
                            compareReplayBatch.dualSelectBothVariants
                            || variantIndex === compareReplayBatch.selectedVariantIndex
                          );
                          return (
                            <button
                              key={`${d.dealIndex}-t${v.tableIdx}`}
                              type="button"
                              className={`training-compare-deal-score ${isSelected ? 'selected' : ''}`}
                              title={v.label}
                              onClick={() => onSelectCompareReplayDeal(index, variantIndex)}
                            >
                              {v.scoreText ?? v.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`training-compare-deal-item ${isSelectedDeal ? 'selected' : ''}`}
                        onClick={() => onSelectCompareReplayDeal(index)}
                      >
                        {d.label}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Settings tab */}
      {activeTab === 'settings' && (
        <div className="training-sidebar-tab-content">
          <div className="training-controls-body">
            {/* Display mode */}
            <CollapsibleSection title="Affichage">
              <div className="training-toggle">
                <span className="training-toggle-label">Cartes sur table</span>
                <button
                  className={`training-switch ${displayMode === 'cards_on_table' ? 'active' : ''}`}
                  onClick={onToggleDisplayMode}
                />
              </div>
              <div className="training-toggle">
                <span className="training-toggle-label">Résumé des plis</span>
                <button
                  className={`training-switch ${showTimeline ? 'active' : ''}`}
                  onClick={onToggleTimeline}
                />
              </div>
              <div className="training-toggle">
                <span className="training-toggle-label">Conseils détaillés</span>
                <button
                  className={`training-switch ${adviceDetailed ? 'active' : ''}`}
                  onClick={onToggleAdviceDetailed}
                />
              </div>
              <button
                className="training-btn training-btn-secondary"
                onClick={onRotateBoard}
                style={{ marginTop: 6, width: '100%', fontSize: 12 }}
              >
                ↻ Tourner le plateau
              </button>
            </CollapsibleSection>

            {/* Speed setting */}
            <CollapsibleSection title="Vitesse de jeu">
              <div className="training-speed-selector">
                {SPEED_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className={`training-speed-btn ${gameSpeed === opt.value ? 'active' : ''}`}
                    onClick={() => onSpeedChange?.(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </CollapsibleSection>

            {/* Bot management */}
            <CollapsibleSection title="Jouerie automatique">
              {[0, 1, 2, 3].map(seat => {
                const player = players.find(p => p.position === seat);
                const isHuman = seat === myPosition;
                const isBotActive = isHuman ? humanAutomated : !disabledBots.has(seat);
                const label = isHuman
                  ? `${player?.pseudo ?? 'Vous'} (${POSITION_NAMES[seat]})`
                  : player?.pseudo ?? `Bot ${seat}`;
                const title = isHuman
                  ? (humanAutomated ? 'Reprendre le contrôle' : 'Laisser le bot jouer à votre place')
                  : (disabledBots.has(seat) ? 'Réactiver le bot' : 'Désactiver le bot (vous jouez à sa place)');
                return (
                  <div key={seat} className="training-bot-row">
                    <span className={`training-bot-name ${!isBotActive ? 'disabled' : ''}`}>
                      {label}
                    </span>
                    <button
                      className={`training-switch ${isBotActive ? 'active' : ''}`}
                      onClick={() => onToggleBot(seat)}
                      title={title}
                    />
                  </div>
                );
              })}
              {useBetaModel && onToggleBotModelSlot && (
                <div className="training-toggle" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <span className="training-toggle-label" style={{ fontSize: 11 }}>
                    Modèle IA : {botModelSlot === 'beta' ? 'Beta' : 'Production'}
                  </span>
                  <button
                    className={`training-switch ${botModelSlot === 'beta' ? 'active' : ''}`}
                    onClick={() => onToggleBotModelSlot(botModelSlot === 'beta' ? 'production' : 'beta')}
                    title={botModelSlot === 'beta' ? 'Utiliser le modèle production pour les bots' : 'Utiliser le modèle beta pour les bots'}
                  />
                </div>
              )}
              {isAdmin && aiModelSelection && onAIModelSelectionChange && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="training-toggle-label" style={{ fontSize: 11, marginBottom: 6 }}>
                    Moteurs IA utilisés
                  </div>
                  {([
                    ['attacker', 'Attaque'],
                    ['defender', 'Défense'],
                    ['bidding', 'Contrat'],
                    ['discard', 'Écart'],
                  ] as Array<[keyof TrainingAIModelSelection, string]>).map(([key, label]) => (
                    <div key={key} className="training-hand-config-row">
                      <label>{label}</label>
                      <select
                        className="training-select"
                        value={aiModelSelection[key]}
                        onChange={e => handleAIModelChange(key, e.target.value as TrainingAIModelSlot)}
                      >
                        {AI_MODEL_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Deal configuration — même composant que l'écran d'accueil */}
            <CollapsibleSection title="Configuration de la donne" defaultOpen={false}>
              <TrainingDealSetup
                variant="sidebar"
                value={dealSetup}
                onChange={onDealSetupChange}
                showForceBid
                forceSeat={forceSeat}
                forceContract={forceContract}
                onForceSeatChange={setForceSeat}
                onForceContractChange={setForceContract}
                onForceBid={handleForceBid}
                players={players}
              />
            </CollapsibleSection>

            {/* Actions */}
            <CollapsibleSection title="Actions">
              <div className="training-actions">
                <button
                  className="training-btn training-btn-primary"
                  onClick={handleNewDealWithConstraints}
                  disabled={!constraintValidation.valid}
                >
                  Nouvelle donne
                </button>
                <button className="training-btn training-btn-secondary" onClick={onRestartSameDeal}>
                  Rejouer la même donne
                </button>
                {onAddToLibrary && (
                  <button className="training-btn training-btn-secondary" onClick={onAddToLibrary}>
                    📚 Ajouter à la bibliothèque
                  </button>
                )}
                <button className="training-btn training-btn-danger" onClick={onAbandon}>
                  Quitter l&apos;entraînement
                </button>
              </div>
            </CollapsibleSection>

            {/* Rebid after all pass */}
            {isWaitingRebid && (
              <CollapsibleSection title="Tout le monde a passé">
                <p style={{ fontSize: 12, color: 'var(--text-muted, rgba(255,255,255,0.6))', margin: '0 0 8px' }}>
                  Vous pouvez voir le chien et relancer les enchères.
                </p>
                <button className="training-btn training-btn-primary" onClick={onRebidAfterPass}>
                  Relancer les enchères
                </button>
              </CollapsibleSection>
            )}
          </div>
        </div>
      )}

      {/* Coach / Professor tab */}
      {activeTab === 'coach' && (
        <div className="training-coach-tab">
          {canShowFeedback && (
            <div className="feedback-toggle-row">
              <span className="feedback-toggle-label">Aider le professeur</span>
              <button
                className={`training-switch ${feedbackMode ? 'active' : ''}`}
                onClick={() => onToggleFeedbackMode?.(!feedbackMode)}
              />
            </div>
          )}

          {feedbackMode && onSubmitFeedback ? (
            <ExpertFeedbackPanel
              mlSuggestion={mlSuggestion ?? null}
              feedbackSeat={feedbackSeat ?? 0}
              feedbackSeatName={players.find(p => p.position === (feedbackSeat ?? 0))?.pseudo ?? `Joueur ${feedbackSeat ?? 0}`}
              legalMoves={legalMoves ?? []}
              hand={hand ?? []}
              trickIdx={trickIdx ?? 0}
              positionInTrick={positionInTrick ?? 0}
              gameState={gameState ?? {}}
              onSubmit={onSubmitFeedback}
              cardClickRef={feedbackCardClickRef}
              taker={taker}
              viewPosition={viewPosition}
            />
          ) : (
            <CoachChat
              socket={socket}
              isPlaying={isPlaying}
              currentSeat={currentPlayer === myPosition ? myPosition : currentPlayer}
              canAskAdvice={canAskAdvice}
              messages={coachMessages}
              onAddMessage={onAddCoachMessage}
              adviceDetailed={adviceDetailed}
              debugML={true}
              taker={taker}
              viewPosition={viewPosition}
            />
          )}
        </div>
      )}
    </div>
  );
}

function getPhaseLabel(phase: string | number): string {
  switch (phase) {
    case 'waiting': case 0: return 'En attente';
    case 'bidding': case 2: return 'Enchères';
    case 'king_call': case 3: return 'Appel au roi';
    case 'chien': case 4: return 'Chien';
    case 'ecart': case 5: return 'Écart';
    case 'playing': case 6: return 'Jeu';
    case 'end_of_deal': case 99999: return 'Fin de donne';
    default: return String(phase);
  }
}
